import { DatePickerWrapper, MainWrapper, ResourcesWrapper, MenuWrapper, TextWrapper } from './calendar-page-navigation.style'
import { Button, DatePicker, Dropdown, InputRef, MenuProps, Typography, message, theme } from 'antd'
import { LoadingOutlined, ReloadOutlined, LeftOutlined, RightOutlined, DownOutlined, CheckOutlined } from '@ant-design/icons'
import { useTranslate, addDays, getDateRange, min, queryClient, timezoneFormatter } from 'utils'
import { useConfig, useCurrentLocation, useResources } from 'api'
import { HorizontalContainer, Loading, NotFound, PageError, VerticalContainer } from 'components/styled'
import { Shop, Resource } from 'types'
import { useEffect, useRef, useState } from 'react'
import { CALENDAR_MAX_RESOURCE_SELECT, CALENDAR_RESOURCE_LSK } from 'app.constants'
import { Avatar } from 'components/avatar/avatar'
import dayjs from 'dayjs'
import { Search } from 'components/search/search'
const { Text } = Typography

const items: MenuProps['items'] = [{ key: '1', label: <div></div> }]

type Props = {
    shop: Shop,
    startDate: Date
    numberOfDays: number
    areSlotsLoading: boolean
    onStartDateChange: (date: Date) => void
    onResourcesChange: (resources: Resource[]) => void
}

export const CalendarPageNavigation = ({
    shop,
    startDate,
    numberOfDays,
    areSlotsLoading,
    onStartDateChange,
    onResourcesChange
}: Props) => {

    //hooks:
    const localStorageIds = getResourcesFromLocalStorage(shop.id)
    const { __ } = useTranslate()
    const container = useRef<HTMLDivElement>(null)
    const searchInputRef = useRef<InputRef>(null)
    const { token: {
        paddingXXS,
        paddingXS,
        paddingSM,
        margin,
        fontSizeSM,
        fontSizeLG,
        colorTextDescription,
        colorBgContainer,
        boxShadowTertiary,
        colorBorder,
        borderRadiusLG
    }} = theme.useToken()

    //state:
    const [ selectedResourcesIds, setSelectedResourcesIds ] = useState<string[]>([])
    const [ isRefreshSelected, setIsRefreshSelected ] = useState(false)
    const [ search, setSearch ] = useState<string | null>(null)
    const [ isTyping, setIsTyping ] = useState(false)
    const { data: currentLocation } = useCurrentLocation()
    const { data: config } = useConfig()
    const timezone = currentLocation ? currentLocation.timezone : config ? config.settings.timezone : null

    //resources:
    const { data: selectedResources, isFetching: isSelectedResourcesLoading } = useResources(
        null,
        null,
        timezone,
        selectedResourcesIds,
        null,
        CALENDAR_MAX_RESOURCE_SELECT,
        selectedResourcesIds.length > 0
    )
    const { data: allResources, isFetching: isAR_Fetching } = useResources(null, null, timezone, [], search)

    // check for locale storage on render:
    useEffect(() => {
        if(localStorageIds.length > 0){
            setSelectedResourcesIds(localStorageIds)
        }
    }, [])

    // if there is no ids in locale storage, pick from all resources list:
    useEffect(() => {
        if(allResources && localStorageIds.length === 0 && container.current){
            const containerWidth = container.current.offsetWidth
            const columnsNumber = min(Math.floor(containerWidth / 150), allResources.length)
            const selectedRSIds: string[] = []
            for(let i=0; i < columnsNumber; i++){
                selectedRSIds.push(allResources[i].id)
            }
            setSelectedResourcesIds(selectedRSIds)
            saveResourcesToLocalStorage(shop.id, selectedRSIds)
        }
    }, [allResources])

    // update the upper component on resource change:
    useEffect(() => {
        if(selectedResources){
            onResourcesChange(selectedResources)
        }
    }, [selectedResources])

    // disable refresh button loading for other fetches:
    useEffect(() => { !areSlotsLoading && setIsRefreshSelected(false) }, [areSlotsLoading])

    const handleSelect = (resource: Resource) => {
        if(selectedResourcesIds.length < CALENDAR_MAX_RESOURCE_SELECT){
            let newResourceIds: string[]
            if(selectedResourcesIds.find(id => id === resource.id)){
                newResourceIds = [...selectedResourcesIds.filter(id => id !== resource.id)]
            }else{
                newResourceIds = [...selectedResourcesIds]
                newResourceIds.push(resource.id)
            }
            setSelectedResourcesIds(newResourceIds)
            saveResourcesToLocalStorage(shop.id, newResourceIds)
        }else{
            message.info(__('the_maximum_selectable_resources_are_limit', { limit : CALENDAR_MAX_RESOURCE_SELECT }))
        }
    }
    
    return (!config || !timezone) ? null : (
        <MainWrapper ref={container}>
            
            <TextWrapper>

                <VerticalContainer style={{ gap: 0 }}>

                    {/* time */}
                    <Text style={{ fontSize: fontSizeLG, fontFamily: 'inter-medium' }}>
                        {getDateRange(startDate, numberOfDays, config, true)}
                    </Text>

                    {/* timezone */}
                    <Text style={{ fontSize: fontSizeSM, color: colorTextDescription }}>
                        {timezoneFormatter(timezone)}
                    </Text>

                </VerticalContainer>
            
            </TextWrapper>

            <MenuWrapper>

                <DatePickerWrapper>

                    {/* date picker */}
                    <DatePicker
                        inputReadOnly
                        defaultValue={dayjs(startDate)}
                        value={dayjs(startDate)}
                        allowClear={false}
                        style={{ width: '100%', height: 'fit-content' }}
                        onChange={val => onStartDateChange(dayjs(val).toDate())}
                    />

                    <HorizontalContainer>

                        {/* left arrow */}
                        <Button
                            icon={<LeftOutlined style={{ fontSize: fontSizeSM }}/>}
                            title={__`previous_day`}
                            size={'middle'}
                            style={{ fontSize: fontSizeSM, backgroundColor: 'transparent' }}
                            onClick={() => onStartDateChange(addDays(startDate, -1))}
                        />
                        
                        {/* right arrow */}
                        <Button
                            icon={<RightOutlined style={{ fontSize: fontSizeSM }}/>}
                            title={__`next_day`}
                            size={'middle'}
                            style={{ fontSize: fontSizeSM, backgroundColor: 'transparent' }}
                            onClick={() => onStartDateChange(addDays(startDate, 1))}
                        />

                        {/* refresh */}
                        <Button
                            icon={(areSlotsLoading && isRefreshSelected) ? <LoadingOutlined style={{ fontSize: fontSizeSM }}/> : <ReloadOutlined style={{ fontSize: fontSizeSM }}/>}
                            title={__`refresh`}
                            size={'middle'}
                            style={{ fontSize: fontSizeSM, backgroundColor: 'transparent' }}
                            onClick={() => {
                                setIsRefreshSelected(true)
                                queryClient.invalidateQueries(['calendarSlots'])
                            }}
                        />
                        
                    </HorizontalContainer>

                </DatePickerWrapper>

                <ResourcesWrapper>

                    {/* resource select */}
                    <Dropdown
                        menu={{ items }}
                        trigger={['click']}
                        dropdownRender={() => (
                            <VerticalContainer style={{
                                width: 240,
                                paddingBottom: paddingXS,
                                backgroundColor: colorBgContainer,
                                border: `solid 1px ${colorBorder}`,
                                borderRadius: borderRadiusLG,
                                boxShadow: boxShadowTertiary
                            }}>

                                <div style={{ padding: `${paddingXS}px ${paddingXS}px ${paddingXXS}px ${paddingXS}px` }}>
                                    <Search
                                        inputRef={searchInputRef}
                                        onType={setIsTyping}
                                        onSearch={setSearch}
                                    />
                                </div>

                                <VerticalContainer style={{ maxHeight: 250, overflowY: 'auto', gap: 0 }}>
                                    {(isTyping || isAR_Fetching) ? <Loading/> : (
                                        !allResources ? <PageError/> : (
                                            (!allResources.length) ? <NotFound/> : (
                                                allResources.map(resource => (

                                                    <Button
                                                        key={resource.id}
                                                        type='text'
                                                        style={{ borderRadius: 0 }}
                                                        title={resource.name}
                                                        onClick={() => handleSelect(resource)}
                                                    >

                                                        <HorizontalContainer style={{ width: '100%' }}>
                                                            
                                                            <Text style={{
                                                                width: '90%',
                                                                textAlign: 'left',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden'
                                                            }}>
                                                                {resource.name}
                                                            </Text>

                                                            {
                                                                selectedResourcesIds.find(id => id === resource.id) ? (
                                                                    <CheckOutlined style={{ fontSize: fontSizeSM }}/>
                                                                ) : <></>
                                                            }

                                                        </HorizontalContainer>

                                                    </Button>

                                                ))
                                            )
                                        )
                                    )}
                                </VerticalContainer>

                            </VerticalContainer>
                        )}
                        onOpenChange={(isOpen: boolean) => {
                            if(isOpen){
                                setTimeout(() => searchInputRef.current?.focus(), 100)
                            }
                        }}
                    >

                        {/* selected resources list */}
                        <Button type='default' style={{ width: 240, paddingLeft: paddingXXS, paddingRight: paddingSM }}>
                            <HorizontalContainer style={{ gap: margin }}>
                                <HorizontalContainer style={{ opacity: isSelectedResourcesLoading ? .5 : 1 }}>
                                    {selectedResources && selectedResources.map((r, i) => i < 6 ? (
                                        <Avatar
                                            key={r.id}
                                            image={r.image.path}
                                            placeholder={r.name[0]}
                                            size='nano'
                                            style={{ padding: 0 }}
                                        />
                                    ) : null)}
                                    {selectedResources && selectedResources.length > 6 && (
                                        <Text>+{selectedResources.length - 6}</Text>
                                    )}
                                </HorizontalContainer>
                                <DownOutlined style={{ fontSize: fontSizeSM }}/>
                            </HorizontalContainer>
                        </Button>

                    </Dropdown>

                </ResourcesWrapper>

            </MenuWrapper>

        </MainWrapper>
    )
    
}

type Storage = {
    shopId: string,
    resourceIds: string[]
}

const getResourcesFromLocalStorage = (shopId: string) => {
    const storageList = getAllShopsResourcesFromLocalStorage()
    const storage = storageList.find(st => st.shopId === shopId)
    return storage?.resourceIds ?? []
}

const getAllShopsResourcesFromLocalStorage = () => {
    let storageList: Storage[] = []
    try{
        const storageListString = localStorage.getItem(CALENDAR_RESOURCE_LSK)
        if(storageListString){
            storageList = JSON.parse(storageListString)
        }
    }catch(e){e}
    return storageList
}

const saveResourcesToLocalStorage = (shopId: string, resourceIds: string[]) => {
    const storageList = getAllShopsResourcesFromLocalStorage()
    let newStorageList = [...storageList]
    const isShopExists = storageList.find(st => st.shopId === shopId)
    if(isShopExists){
        newStorageList = storageList.map(st => {
            if(st.shopId === shopId){
                return { ...st, resourceIds }
            }else{
                return st
            }
        })
    }else{
        newStorageList.push({ shopId, resourceIds })
    }
    try{
        localStorage.setItem(CALENDAR_RESOURCE_LSK, JSON.stringify(newStorageList))
    }catch(e){e}
}
